import { useMemo } from 'react';

import {
  sectionSelectors,
  useSelectWidgetInstanceEntities,
} from '../../slices';

import type { WidgetType } from '../../widgetManager';

/**
 * Hook to count placed widgets by type and section
 */
export const useWidgetCounting = () => {
  const sectionsById = sectionSelectors.useSelectSectionEntities();
  const widgetInstances = useSelectWidgetInstanceEntities();

  return useMemo(() => {
    const countWidgets = (widgetType: WidgetType) => {
      // Get all widget IDs that are currently placed in sections
      const placedWidgetIds = new Set<string>();
      Object.values(sectionsById).forEach(section => {
        if (section?.blockOrder) {
          section.blockOrder.forEach(widgetId => placedWidgetIds.add(widgetId));
        }
      });

      // Count existing widgets of this type that are actually placed
      const counts = Object.values(widgetInstances).reduce(
        (acc, instance) => {
          // Only count if the widget is placed in a section AND matches the type
          if (
            instance &&
            placedWidgetIds.has(instance.id) &&
            instance.type === widgetType
          ) {
            acc.total += 1;
            if (instance.sectionId) {
              acc.bySection[instance.sectionId] =
                (acc.bySection[instance.sectionId] || 0) + 1;
            }
          }
          return acc;
        },
        { total: 0, bySection: {} as Record<string, number> },
      );

      return counts;
    };

    return { countWidgets };
  }, [sectionsById, widgetInstances]);
};
